import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Minus, Plus, Trash2, CheckCircle2 } from "lucide-react";

export default function POS() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("All");
  const [cart, setCart] = useState([]);
  const [customerName, setCustomerName] = useState("Walk-in");
  const [placing, setPlacing] = useState(false);

  useEffect(() => {
    api.get("/menu", user?.store_id ? { params: { store_id: user.store_id } } : undefined).then((r) => setItems(r.data));
  }, [user?.store_id]);

  const categories = useMemo(() => ["All", ...Array.from(new Set(items.map((i) => i.category)))], [items]);
  const filtered = items.filter((i) => (category === "All" || i.category === category) && i.is_available);

  const add = (it) => {
    setCart((c) => {
      const f = c.find((x) => x.item_id === it.id);
      if (f) return c.map((x) => x.item_id === it.id ? { ...x, quantity: x.quantity + 1 } : x);
      return [...c, { item_id: it.id, name: it.name, price: it.price, quantity: 1 }];
    });
  };
  const dec = (id) => setCart((c) => c.map((x) => x.item_id === id ? { ...x, quantity: x.quantity - 1 } : x).filter((x) => x.quantity > 0));
  const rm = (id) => setCart((c) => c.filter((x) => x.item_id !== id));

  const subtotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);
  const tax = subtotal * 0.08;
  const total = subtotal + tax;

  const place = async () => {
    if (cart.length === 0) return;
    setPlacing(true);
    try {
      const { data } = await api.post("/orders", {
        items: cart, fulfillment: "pickup", location: "POS Counter",
        notes: "", payment_method: "pay_at_counter", customer_name: customerName || "Walk-in",
      });
      toast.success(<span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4"/>Order {data.order_number} placed</span>);
      setCart([]); setCustomerName("Walk-in");
    } catch { toast.error("Failed"); } finally { setPlacing(false); }
  };

  const catColor = {
    "Sushi & Sashimi": "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30",
    "Signature Rolls": "bg-purple-100 text-purple-700 border-purple-200",
    "Poke Bowls": "bg-emerald-100 text-emerald-700 border-emerald-200",
    "Ramen": "bg-amber-100 text-amber-700 border-amber-200",
    "Bento Boxes": "bg-blue-100 text-blue-700 border-blue-200",
    "Sides": "bg-neutral-100 text-neutral-700 border-neutral-200",
  };

  return (
    <div className="h-screen flex overflow-hidden bg-neutral-50">
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="p-6 border-b border-neutral-200 bg-white">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="eyebrow">Point of Sale</p>
              <h1 className="font-display font-bold text-2xl tracking-tight" data-testid="pos-title">Take Order</h1>
            </div>
          </div>
          <div className="flex gap-2 overflow-x-auto -mx-2 px-2">
            {categories.map((c) => (
              <button key={c} onClick={() => setCategory(c)} className={`whitespace-nowrap px-4 h-9 rounded-lg text-sm font-medium border ${category === c ? "bg-neutral-900 text-white border-neutral-900" : "bg-white border-neutral-200 hover:border-neutral-400"}`} data-testid={`pos-cat-${c}`}>{c}</button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
            {filtered.map((it) => (
              <button key={it.id} onClick={() => add(it)} className={`text-left bg-white rounded-xl border-2 border-neutral-200 hover:border-[hsl(var(--primary))] transition-colors overflow-hidden`} data-testid={`pos-item-${it.id}`}>
                <div className="aspect-[4/3] bg-neutral-100"><img src={it.image_url} alt="" className="w-full h-full object-cover"/></div>
                <div className="p-3">
                  <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${catColor[it.category] || "bg-neutral-100"}`}>{it.category.split(" ")[0]}</span>
                  <h3 className="font-semibold text-sm mt-2 leading-tight">{it.name}</h3>
                  <div className="font-display font-bold text-[hsl(var(--primary))] mt-1">${it.price.toFixed(2)}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <aside className="w-96 flex flex-col bg-white border-l border-neutral-200">
        <div className="p-5 border-b border-neutral-200">
          <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Ticket</p>
          <Input value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Customer name" data-testid="pos-customer-name"/>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {cart.length === 0 ? (
            <div className="text-center py-16 text-neutral-400 text-sm" data-testid="pos-empty-cart">No items yet.<br/>Tap items to add.</div>
          ) : cart.map((it) => (
            <div key={it.item_id} className="flex items-center gap-2" data-testid={`pos-cart-${it.item_id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{it.name}</div>
                <div className="text-xs text-neutral-500">${it.price.toFixed(2)}</div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => dec(it.item_id)} className="w-7 h-7 rounded bg-neutral-100 flex items-center justify-center"><Minus className="w-3 h-3"/></button>
                <span className="w-6 text-center text-sm font-semibold">{it.quantity}</span>
                <button onClick={() => add({id: it.item_id, name: it.name, price: it.price})} className="w-7 h-7 rounded bg-neutral-100 flex items-center justify-center"><Plus className="w-3 h-3"/></button>
              </div>
              <div className="w-14 text-right font-semibold text-sm">${(it.price * it.quantity).toFixed(2)}</div>
              <button onClick={() => rm(it.item_id)} className="text-neutral-400 hover:text-red-600"><Trash2 className="w-4 h-4"/></button>
            </div>
          ))}
        </div>
        <div className="p-5 border-t border-neutral-200 space-y-2">
          <div className="flex justify-between text-sm text-neutral-500"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
          <div className="flex justify-between text-sm text-neutral-500"><span>Tax 8%</span><span>${tax.toFixed(2)}</span></div>
          <div className="flex justify-between font-display font-bold text-xl"><span>Total</span><span data-testid="pos-total">${total.toFixed(2)}</span></div>
          <Button onClick={place} disabled={placing || cart.length === 0} className="w-full h-12 rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-base mt-2" data-testid="pos-place-order">
            {placing ? "Placing…" : "Charge & Place Order"}
          </Button>
        </div>
      </aside>
    </div>
  );
}
